import { FC } from 'react';
import { X } from 'lucide-react';

import { useTranslation } from '@app/app/i18n';
import { Button } from '@app/shared/ui/button';

interface InitialPopoverProps {
  variant: 'initial';
  onDismiss: () => void;
}

interface StepPopoverProps {
  variant: 'step';
  titleKey: string;
  descriptionKey: string;
  onDismiss: () => void;
  /** If provided, renders a "Skip" button */
  onSkip?: () => void;
  /** If provided, renders a "Next"/"Done" action button */
  actionLabelKey?: string;
  onAction?: () => void;
}

export type ImageGenPopoverProps = InitialPopoverProps | StepPopoverProps;

export const ImageGenPopover: FC<ImageGenPopoverProps> = (props) => {
  const { t } = useTranslation('finalize-stage');

  if (props.variant === 'initial') {
    return (
      <div className="flex flex-col gap-3 p-4 max-w-[280px] bg-general_background_MIII_500_dark rounded-xl">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-sm font-semibold text-general_text_MII_secondary_i">
            {t('onboarding.tours.imageGen.initial.title')}
          </h3>
          <button
            onClick={props.onDismiss}
            className="shrink-0 p-0.5 rounded hover:opacity-80 transition-opacity"
            aria-label={t('onboarding.fab.close', { defaultValue: 'Close' })}
          >
            <X className="w-4 h-4 text-general_text_MII_secondary_i" />
          </button>
        </div>

        <p className="text-xs text-general_text_MII_secondary_i leading-relaxed">
          {t('onboarding.tours.imageGen.initial.description')}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 p-4 max-w-[280px] bg-general_background_MIII_500_dark rounded-xl">
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-sm font-semibold text-general_text_MII_secondary_i">
          {t(props.titleKey)}
        </h3>
        <button
          onClick={props.onDismiss}
          className="shrink-0 p-0.5 rounded hover:opacity-80 transition-opacity"
          aria-label={t('onboarding.fab.close', { defaultValue: 'Close' })}
        >
          <X className="w-4 h-4 text-general_text_MII_secondary_i" />
        </button>
      </div>

      <p className="text-xs text-general_text_MII_secondary_i leading-relaxed">
        {t(props.descriptionKey)}
      </p>

      {(props.onSkip || (props.actionLabelKey && props.onAction)) && (
        <div className="flex items-center justify-end gap-2 mt-1">
          {props.onSkip && (
            <Button variant="ghost" size="text14" onClick={props.onSkip} className="text-xs">
              {t('onboarding.tours.imageGen.cancel')}
            </Button>
          )}
          {props.actionLabelKey && props.onAction && (
            <Button
              variant="primary"
              size="text14"
              onClick={props.onAction}
              className="text-xs px-4 py-1.5"
            >
              {t(props.actionLabelKey)}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
